import { useCallback, useState } from "react";
import type { FeedbackConfig, FeedbackKind } from "@/shared/api";
import type { FeedbackFormValues } from "./FeedbackDialog.types";

interface Options {
  limits: FeedbackConfig;
  onSubmit: (values: FeedbackFormValues) => void;
}

/** Shortest summary worth filing. Below this the issue title says nothing. */
const MIN_SUMMARY = 3;

/**
 * Form state for a problem report.
 *
 * The validation is as light as it can be, on purpose. Every field but the
 * summary is optional, and the only rule is that the summary is long enough to
 * become a title somebody can read in a list. A reporter who has just watched
 * the app fail should not then have to satisfy a form.
 *
 * `expected` is kept across a change of kind rather than cleared. Somebody who
 * fills in a bug, then wonders whether it is really an idea, then goes back,
 * should not find their answer gone - and it is only ever *sent* for a bug.
 */
export function useFeedbackForm({ limits, onSubmit }: Options) {
  const [kind, setKind] = useState<FeedbackKind>("bug");
  const [summary, setSummary] = useState("");
  const [detail, setDetail] = useState("");
  const [expected, setExpected] = useState("");

  const trimmed = summary.trim();

  // Shown only once they have started typing. An error sitting on an untouched
  // field reads as a telling-off before they have done anything.
  const summaryError =
    trimmed.length > 0 && trimmed.length < MIN_SUMMARY ? "A few more words, please." : null;

  const canSubmit = trimmed.length >= MIN_SUMMARY;

  const submit = useCallback(() => {
    if (summary.trim().length < MIN_SUMMARY) return;
    onSubmit({
      kind,
      summary: summary.trim().slice(0, limits.max_summary_length),
      detail: detail.slice(0, limits.max_detail_length),
      expected: kind === "bug" ? expected.slice(0, limits.max_detail_length) : "",
    });
  }, [kind, summary, detail, expected, limits, onSubmit]);

  const reset = useCallback(() => {
    setKind("bug");
    setSummary("");
    setDetail("");
    setExpected("");
  }, []);

  return {
    kind,
    summary,
    detail,
    expected,
    summaryError,
    canSubmit,
    setKind,
    setSummary,
    setDetail,
    setExpected,
    submit,
    reset,
  };
}
